/**
 * @fileoverview Pipeline Analysis Page
 * @description Initial UI for selecting an Azure DevOps project
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  TextField,
  Alert,
  CircularProgress,
  Stack,
  Button,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { GitBranch, RefreshCw } from 'lucide-react';
import { pipelineAnalysisApi } from '../services/pipelineAnalysisApi';
import { appInsights } from '@/telemetry';
import './PipelineAnalysis.css';

export const PipelineAnalysis = () => {
  const [projects, setProjects] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [pipelineRuns, setPipelineRuns] = useState([]);
  const [releases, setReleases] = useState([]);
  const [runDetails, setRunDetails] = useState([]);

  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedPipelineId, setSelectedPipelineId] = useState('');
  const [selectedBuildId, setSelectedBuildId] = useState('');
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [pipelineSearchTerm, setPipelineSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('pipeline-runs');

  const [analyzingDetailId, setAnalyzingDetailId] = useState('');
  const [analysisTarget, setAnalysisTarget] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isRunDetailsDialogOpen, setIsRunDetailsDialogOpen] = useState(false);
  const [activeRun, setActiveRun] = useState(null);
  const [showOnlyFailed, setShowOnlyFailed] = useState(false);
  const analysisSectionRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [loadingReleases, setLoadingReleases] = useState(false);
  const [loadingRunDetails, setLoadingRunDetails] = useState(false);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);

  const [error, setError] = useState(null);
  const [flowError, setFlowError] = useState(null);

  const selectedProject = useMemo(
    () => projects.find((project) => project.id === selectedProjectId) || null,
    [projects, selectedProjectId]
  );

  const selectedPipeline = useMemo(
    () => pipelines.find((pipeline) => String(pipeline.id) === String(selectedPipelineId)) || null,
    [pipelines, selectedPipelineId]
  );

  const filteredProjects = useMemo(() => {
    const searchTerm = projectSearchTerm.trim().toLowerCase();
    if (!searchTerm) return projects;

    return projects.filter((project) =>
      String(project?.name || '')
        .toLowerCase()
        .includes(searchTerm)
    );
  }, [projects, projectSearchTerm]);

  const visibleProjects = useMemo(() => {
    if (!selectedProjectId) return filteredProjects;

    const hasSelected = filteredProjects.some((project) => project.id === selectedProjectId);
    if (hasSelected) return filteredProjects;

    const selected = projects.find((project) => project.id === selectedProjectId);
    return selected ? [selected, ...filteredProjects] : filteredProjects;
  }, [filteredProjects, projects, selectedProjectId]);

  const filteredPipelines = useMemo(() => {
    const searchTerm = pipelineSearchTerm.trim().toLowerCase();
    if (!searchTerm) return pipelines;

    return pipelines.filter((pipeline) =>
      String(pipeline?.name || `Pipeline ${pipeline?.id || ''}`)
        .toLowerCase()
        .includes(searchTerm)
    );
  }, [pipelines, pipelineSearchTerm]);

  const visiblePipelines = useMemo(() => {
    if (!selectedPipelineId) return filteredPipelines;

    const hasSelected = filteredPipelines.some(
      (pipeline) => String(pipeline.id) === String(selectedPipelineId)
    );
    if (hasSelected) return filteredPipelines;

    const selected = pipelines.find((pipeline) => String(pipeline.id) === String(selectedPipelineId));
    return selected ? [selected, ...filteredPipelines] : filteredPipelines;
  }, [filteredPipelines, pipelines, selectedPipelineId]);

  const getRunId = (run) => String(run?.id ?? run?.runId ?? '');
  const getRunResult = (run) => run?.result || run?.state || 'unknown';
  const normalizeResult = (value) => String(value || 'unknown').replace(/\s+/g, '').toLowerCase();
  const formatResultLabel = (value) => {
    const normalized = normalizeResult(value);

    if (normalized === 'partiallysucceeded') {
      return 'Partially Succeeded';
    }

    const cleaned = String(value || 'Unknown').replace(/_/g, ' ').trim();
    return cleaned
      .split(' ')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };
  const getResultChipColor = (value) => {
    const normalized = normalizeResult(value);

    if (['succeeded', 'passed', 'pass'].includes(normalized)) return 'success';
    if (['failed', 'fail', 'error'].includes(normalized)) return 'error';
    if (['partiallysucceeded', 'partiallypassed', 'warning'].includes(normalized)) return 'warning';
    if (['running', 'inprogress', 'queued', 'pending'].includes(normalized)) return 'info';

    return 'default';
  };
  const isFailedOutcome = (value) => {
    const normalized = normalizeResult(value);
    return ['failed', 'fail', 'error'].includes(normalized);
  };
  const filteredRunDetails = useMemo(() => {
    if (!showOnlyFailed) return runDetails;
    return runDetails.filter((detail) => isFailedOutcome(detail.outcome));
  }, [runDetails, showOnlyFailed]);
  const getReleaseDisplayName = (release) =>
    release?.releaseName ||
    release?.releaseDefinition?.name ||
    release?.definition?.name ||
    '-';

  const parseArrayResponse = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.value)) return response.value;
    return [];
  };

  const resetDownstream = () => {
    setPipelines([]);
    setPipelineRuns([]);
    setReleases([]);
    setRunDetails([]);
    setPipelineSearchTerm('');
    setSelectedPipelineId('');
    setSelectedBuildId('');
    setAnalyzingDetailId('');
    setAnalysisTarget(null);
    setAnalysisResult(null);
  };

  const loadProjects = async ({ selectFirstIfEmpty = false, preserveSelection = true } = {}) => {
    setLoading(true);
    setError(null);

    try {
      const response = await pipelineAnalysisApi.fetchProjects();
      const data = parseArrayResponse(response);
      setProjects(data);

      const currentProjectId = preserveSelection ? selectedProjectId : '';
      const hasCurrentSelection =
        Boolean(currentProjectId) && data.some((project) => project.id === currentProjectId);
      const resolvedSelectedProjectId = hasCurrentSelection
        ? currentProjectId
        : selectFirstIfEmpty && data.length > 0
          ? data[0].id
          : '';

      setSelectedProjectId(resolvedSelectedProjectId);

      return resolvedSelectedProjectId;
    } catch (err) {
      setError(err.message || 'Failed to fetch projects.');
      appInsights.trackException({
        exception: err,
        properties: { source: 'PipelineAnalysis', action: 'loadProjects' }
      });
      return '';
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    // Reset pipeline/run-level state so refresh always rehydrates from latest backend data.
    setSelectedPipelineId('');
    setSelectedBuildId('');
    setAnalyzingDetailId('');
    setAnalysisTarget(null);
    setAnalysisResult(null);
    setRunDetails([]);
    setPipelineRuns([]);

    const projectId = await loadProjects({
      selectFirstIfEmpty: true,
      preserveSelection: true,
    });

    if (!projectId) {
      resetDownstream();
      return;
    }

    await loadProjectData(projectId);
  };

  const loadProjectData = async (projectId) => {
    if (!projectId) return;

    setFlowError(null);
    setLoadingPipelines(true);
    setLoadingReleases(true);

    try {
      const [pipelinesResponse, releasesResponse] = await Promise.all([
        pipelineAnalysisApi.fetchPipelines(projectId),
        pipelineAnalysisApi.fetchReleases(projectId),
      ]);

      setPipelines(parseArrayResponse(pipelinesResponse));
      setReleases(parseArrayResponse(releasesResponse));
    } catch (err) {
      setFlowError(err.message || 'Failed to load pipelines/releases for selected project.');
      appInsights.trackException({
        exception: err,
        properties: { source: 'PipelineAnalysis', action: 'loadProjectData', projectId }
      });
    } finally {
      setLoadingPipelines(false);
      setLoadingReleases(false);
    }
  };

  const loadPipelineRuns = async (projectId, pipelineId) => {
    if (!projectId || !pipelineId) return;

    setFlowError(null);
    setLoadingRuns(true);

    try {
      const response = await pipelineAnalysisApi.fetchPipelineRuns(projectId, pipelineId);
      setPipelineRuns(parseArrayResponse(response));
    } catch (err) {
      setFlowError(err.message || 'Failed to load pipeline runs.');
      appInsights.trackException({
        exception: err,
        properties: { source: 'PipelineAnalysis', action: 'loadPipelineRuns', projectId, pipelineId }
      });
      setPipelineRuns([]);
    } finally {
      setLoadingRuns(false);
    }
  };

  const loadRunDetails = async (buildIdOverride) => {
    const targetBuildId = String(buildIdOverride ?? selectedBuildId ?? '');
    if (!selectedProjectId || !targetBuildId) return;

    setSelectedBuildId(targetBuildId);

    setFlowError(null);
    setLoadingRunDetails(true);
    setRunDetails([]);
    setAnalyzingDetailId('');
    setAnalysisTarget(null);
    setAnalysisResult(null);

    try {
      const response = await pipelineAnalysisApi.fetchRunDetails(selectedProjectId, targetBuildId);
      setRunDetails(parseArrayResponse(response));
    } catch (err) {
      setFlowError(
        err.message ||
          'Failed to load run details. Backend may need a run-details fix for org/project binding.'
      );
      appInsights.trackException({
        exception: err,
        properties: { source: 'PipelineAnalysis', action: 'loadRunDetails', projectId: selectedProjectId, buildId: targetBuildId }
      });
    } finally {
      setLoadingRunDetails(false);
    }
  };

  const handleBuildRunClick = async (run) => {
    const runId = getRunId(run);
    if (!runId) return;

    setActiveRun(run);
    setIsRunDetailsDialogOpen(true);
    await loadRunDetails(runId);
  };

  const closeRunDetailsDialog = () => {
    setIsRunDetailsDialogOpen(false);
    setRunDetails([]);
    setShowOnlyFailed(false);
    setAnalyzingDetailId('');
    setAnalysisTarget(null);
    setAnalysisResult(null);
  };

  const scrollToAnalysisSection = () => {
    requestAnimationFrame(() => {
      analysisSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  const handleAnalyze = async (detail) => {
    if (!detail) return;

    const detailId = String(detail.id);

    setAnalyzingDetailId(detailId);
    setAnalysisTarget(detail);
    setLoadingAnalysis(true);
    setFlowError(null);
    setAnalysisResult(null);
    scrollToAnalysisSection();

    try {
      const response = await pipelineAnalysisApi.analyzeFailure(
        detail.errorMessage || 'No error message provided',
        detail.stackTrace || 'No stack trace provided'
      );

      const analysisText = response?.choices?.[0]?.message?.content || JSON.stringify(response, null, 2);
      setAnalysisResult(analysisText);
      scrollToAnalysisSection();
    } catch (err) {
      setFlowError(err.message || 'Failed to analyze run details.');
      appInsights.trackException({
        exception: err,
        properties: { source: 'PipelineAnalysis', action: 'handleAnalyze', detailId: detail.id }
      });
    } finally {
      setLoadingAnalysis(false);
      setAnalyzingDetailId('');
    }
  };

  useEffect(() => {
    loadProjects();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    resetDownstream();
    if (selectedProjectId) {
      loadProjectData(selectedProjectId);
    }
  }, [selectedProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPipelineRuns([]);
    setSelectedBuildId('');
    setRunDetails([]);
    setAnalyzingDetailId('');
    setAnalysisTarget(null);
    setAnalysisResult(null);
    setActiveTab('pipeline-runs');

    if (selectedProjectId && selectedPipelineId) {
      loadPipelineRuns(selectedProjectId, selectedPipelineId);
    }
  }, [selectedProjectId, selectedPipelineId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box className="pipeline-analysis-page" sx={{ p: 3 }}>
      <Paper className="pipeline-analysis-card" elevation={0}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5}>
          <Box>
            <Typography variant="h4" fontWeight={700} color="primary.main" className="pipeline-analysis-title">
              <GitBranch size={30} style={{ verticalAlign: 'middle', marginRight: 10 }} />
              Pipeline Service
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Select an Azure DevOps project to continue.
            </Typography>
          </Box>

          <Button
            variant="outlined"
            size="small"
            startIcon={loading ? <CircularProgress size={14} /> : <RefreshCw size={14} />}
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </Stack>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {flowError && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setFlowError(null)}>
            {flowError}
          </Alert>
        )}

        <Stack spacing={2.5}>
          <FormControl fullWidth disabled={loading || projects.length === 0}>
            <InputLabel id="pipeline-project-select-label">Select Project</InputLabel>
            <Select
              labelId="pipeline-project-select-label"
              id="pipeline-project-select"
              value={selectedProjectId}
              label="Select Project"
              onChange={(event) => setSelectedProjectId(event.target.value)}
              onClose={() => setProjectSearchTerm('')}
              MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
            >
              <ListSubheader>
                <TextField
                  autoFocus
                  size="small"
                  placeholder="Search project..."
                  fullWidth
                  value={projectSearchTerm}
                  onChange={(event) => setProjectSearchTerm(event.target.value)}
                  onKeyDown={(event) => event.stopPropagation()}
                />
              </ListSubheader>
              {visibleProjects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
              {filteredProjects.length === 0 && (
                <MenuItem disabled value="__no_project_results__">
                  No matching projects
                </MenuItem>
              )}
            </Select>
          </FormControl>

          {loading && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: -1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Loading projects...
              </Typography>
            </Stack>
          )}

          <FormControl fullWidth disabled={!selectedProjectId || loadingPipelines || pipelines.length === 0}>
            <InputLabel id="pipeline-select-label">Select Pipeline</InputLabel>
            <Select
              labelId="pipeline-select-label"
              id="pipeline-select"
              value={selectedPipelineId}
              label="Select Pipeline"
              onChange={(event) => setSelectedPipelineId(event.target.value)}
              onClose={() => setPipelineSearchTerm('')}
              MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
            >
              <ListSubheader>
                <TextField
                  autoFocus
                  size="small"
                  placeholder="Search pipeline..."
                  fullWidth
                  value={pipelineSearchTerm}
                  onChange={(event) => setPipelineSearchTerm(event.target.value)}
                  onKeyDown={(event) => event.stopPropagation()}
                />
              </ListSubheader>
              {visiblePipelines.map((pipeline) => (
                <MenuItem key={pipeline.id} value={String(pipeline.id)}>
                  {pipeline.name || `Pipeline ${pipeline.id}`}
                </MenuItem>
              ))}
              {filteredPipelines.length === 0 && (
                <MenuItem disabled value="__no_pipeline_results__">
                  No matching pipelines
                </MenuItem>
              )}
            </Select>
          </FormControl>

          {loadingPipelines && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: -1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Loading pipelines for selected project...
              </Typography>
            </Stack>
          )}

          {!loadingPipelines && selectedProjectId && pipelines.length === 0 && (
            <Alert severity="info">No pipelines found for selected project.</Alert>
          )}

          {!selectedPipelineId ? (
            <Alert severity="info">Select a pipeline to view pipeline runs and releases.</Alert>
          ) : (
            <>
              <Tabs
                className="pipeline-tabs"
                value={activeTab}
                onChange={(_event, nextValue) => setActiveTab(nextValue)}
                variant="fullWidth"
              >
                <Tab label={`Pipeline Runs (${pipelineRuns.length})`} value="pipeline-runs" />
                <Tab label={`Releases (${releases.length})`} value="releases" />
              </Tabs>

              {activeTab === 'pipeline-runs' && (
                <>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Build/Run Results
                  </Typography>

                  {loadingRuns ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CircularProgress size={18} />
                      <Typography variant="body2" color="text.secondary">
                        Loading build/run results...
                      </Typography>
                    </Stack>
                  ) : pipelineRuns.length === 0 ? (
                    <Alert severity="info">No builds found for selected pipeline.</Alert>
                  ) : (
                    <Box className="pipeline-analysis-table-wrap">
                      <Table size="small" className="pipeline-data-table">
                        <TableHead>
                          <TableRow>
                            <TableCell>Build/Run Name</TableCell>
                            <TableCell>Run ID</TableCell>
                            <TableCell>Result</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pipelineRuns.map((run) => {
                            const runId = getRunId(run);
                            const runResult = getRunResult(run);

                            return (
                              <TableRow
                                key={runId || run?.name}
                                selected={String(selectedBuildId) === runId}
                                hover
                                sx={{ cursor: runId ? 'pointer' : 'default' }}
                                onClick={() => handleBuildRunClick(run)}
                              >
                                <TableCell>{run?.name || `Run ${runId}`}</TableCell>
                                <TableCell>{runId || '-'}</TableCell>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    className="pipeline-result-chip"
                                    color={getResultChipColor(runResult)}
                                    label={formatResultLabel(runResult)}
                                    variant="outlined"
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                </>
              )}

              {activeTab === 'releases' && (
                <>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Releases
                  </Typography>

                  {loadingReleases ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CircularProgress size={18} />
                      <Typography variant="body2" color="text.secondary">
                        Loading releases...
                      </Typography>
                    </Stack>
                  ) : releases.length === 0 ? (
                    <Alert severity="info">No releases found for selected project.</Alert>
                  ) : (
                    <Box className="pipeline-analysis-table-wrap">
                      <Table size="small" className="pipeline-data-table">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Release Name</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {releases.map((release) => (
                            <TableRow key={String(release.id)}>
                              <TableCell>{release?.name || '-'}</TableCell>
                              <TableCell>{getReleaseDisplayName(release)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                </>
              )}
            </>
          )}

          <Chip
            color="info"
            variant="outlined"
            label={
              loadingPipelines || loadingRuns || loadingReleases
                ? 'Loading project data...'
                : `Pipelines: ${pipelines.length} | Runs: ${pipelineRuns.length} | Releases: ${releases.length}`
            }
          />
        </Stack>

        {!loading && projects.length === 0 && !error && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No projects were returned from the service.
          </Alert>
        )}

        <Divider sx={{ my: 2.5 }} />

        <Dialog
          open={isRunDetailsDialogOpen}
          onClose={closeRunDetailsDialog}
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle>
            Test Results for {activeRun?.name || `Run ${selectedBuildId}`}
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Run ID: {selectedBuildId || '-'} | Result: {getRunResult(activeRun || {})}
            </Typography>

            <FormControlLabel
              sx={{
                mb: 1.5,
                ml: 0,
                mr: 0,
                alignItems: 'center',
                '.MuiFormControlLabel-label': {
                  color: 'text.primary',
                },
              }}
              control={
                <Switch
                  size="small"
                  checked={showOnlyFailed}
                  onChange={(event) => setShowOnlyFailed(event.target.checked)}
                  sx={{ mr: 0.75 }}
                />
              }
              label={
                <Typography variant="body2" fontWeight={600}>
                  Show only failed
                </Typography>
              }
            />

            {loadingRunDetails ? (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Loading test results...
                </Typography>
              </Stack>
            ) : filteredRunDetails.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                {showOnlyFailed
                  ? 'No failed test results found for this build/run.'
                  : 'No run details found for selected build.'}
              </Alert>
            ) : (
              <Box className="pipeline-analysis-table-wrap">
                <Table size="small" className="pipeline-data-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Test Case</TableCell>
                      <TableCell>Outcome</TableCell>
                      <TableCell>Run</TableCell>
                      <TableCell>Started</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRunDetails.slice(0, 50).map((detail) => {
                      const canAnalyze = isFailedOutcome(detail.outcome);

                      return (
                        <TableRow key={String(detail.id)}>
                          <TableCell>{detail.testCaseName || `Case ${detail.id}`}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              className="pipeline-result-chip"
                              color={getResultChipColor(detail.outcome)}
                              label={formatResultLabel(detail.outcome || 'Unknown')}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{detail.testRunName || detail.testRunId || '-'}</TableCell>
                          <TableCell>{detail.startedDate || '-'}</TableCell>
                          <TableCell align="right">
                            <Tooltip
                              title={
                                canAnalyze
                                  ? 'Analyze failed test case'
                                  : 'Available only for failed test cases'
                              }
                              arrow
                            >
                              <span>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="secondary"
                                  onClick={() => handleAnalyze(detail)}
                                  disabled={loadingAnalysis || !canAnalyze}
                                  startIcon={
                                    loadingAnalysis && analyzingDetailId === String(detail.id)
                                      ? <CircularProgress size={14} />
                                      : null
                                  }
                                >
                                  {loadingAnalysis && analyzingDetailId === String(detail.id)
                                    ? 'Analyzing...'
                                    : 'Analyze'}
                                </Button>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}

            {(loadingAnalysis || analysisResult) && (
              <Paper
                ref={analysisSectionRef}
                variant="outlined"
                className="pipeline-analysis-selection"
                sx={{ mt: 2 }}
              >
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  AI-Analysis
                </Typography>

                {loadingAnalysis ? (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Analyzing selected test result...
                    </Typography>
                  </Stack>
                ) : (
                  <Typography
                    variant="body2"
                    component="pre"
                    className="pipeline-analysis-pre"
                  >
                    {analysisResult}
                  </Typography>
                )}
              </Paper>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 1.5 }}>
            <Button onClick={closeRunDetailsDialog}>Close</Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
};

export default PipelineAnalysis;
